import { DrugModel } from '@/models/drug';

interface baseModel<T extends EventType, M extends object> {
  $id?: string;
  type: T;
  occurredAt: string | Date;
  comment: string;
  meta: M;
  drugs: null | EventDrugJoinModel[];
  status: EventStatus;
  isOnTime: boolean;
  $patientId?: string;
  $centerId?: string;
  $doctorId?: string;
  $updatedAt?: string;
  $createdAt?: string;
};

export type OperationModel = baseModel<EventType.Operation, {
  type: string;
  establishment: string;
  department: string;
  doctorName: string;
  doctorComment?: string;
  complications?: string;
}>;

export enum BleedingEventType {
  Articular = 'articular',
  Skin = 'skin',
  Gums = 'gums',
  Custom = 'custom'
}

export enum BleedingEventReason {
  Spontaneous = 'spontaneous',
  Trauma = 'trauma',
  Custom = 'custom',
}

export enum BleedingEventBodyAreaSide {
  Left = 'left',
  Right = 'right',
}

export enum BleedingEventBodyAreaArea {
  Face = 'face',
  Forehead = 'forehead',
  Eye = 'eye',
  Nose = 'nose',
  Ear = 'ear',
  Mouth = 'mouth',
  Neck = 'neck',
  Chest = 'chest',
  Belly = 'belly',
  Hip = 'hip',
  Flank = 'flank',
  Genital = 'genital',
  UpperBack = 'upper_back',
  LowerBack = 'lower_back',
  Shoulder = 'shoulder',
  UpperArm = 'upper_arm',
  Elbow = 'elbow',
  Forearm = 'forearm',
  Wrist = 'wrist',
  Hand = 'hand',
  Fingers = 'fingers',
  Thigh = 'thigh',
  Knee = 'knee',
  Leg = 'leg',
  Ankle = 'ankle',
  Sole = 'sole',
  Toes = 'toes',
}

export type BleedingModel = baseModel<EventType.Bleeding, {
  intensity: number;
  type: BleedingEventType;
  reason: BleedingEventReason;
  customType?: string;
  customReason?: string;
  bodyArea: {
    side: BleedingEventBodyAreaSide;
    area: BleedingEventBodyAreaArea;
  };
}>;

export enum InjectionEventReason {
  Prophylaxis = 'prophylaxis',
  SpecialTreatment = 'specialTreatment',
}

export type InjectionModel = baseModel<EventType.Injection, {
  reason: InjectionEventReason;
}>;

export enum InspectionEventType {
  Hepatology = 'hepatology',
  Ortopedia = 'ortopedia',
  VirusImmunology = 'virusImmunology',
  Custom = 'custom'
}

export type InspectionModel = baseModel<EventType.Inspection, {
  type: InspectionEventType.Hepatology | InspectionEventType.Ortopedia | InspectionEventType.VirusImmunology;
} | {
  type: InspectionEventType.Custom;
  customType: string;
}>;
export type SportModel = baseModel<EventType.Sport, {
  type: string;
  duration: number;
  intensity?: number;
}>;
export type PhysiotherapyModel = baseModel<EventType.Physiotherapy, {
  hjhs?: any,
  hjhsTotal: number;
  physiotherapist: string;
  physiotherapistComment?: string;
}>;
export type NoteModel = baseModel<EventType.Note, {
}>;
export type MissedInjectionModel = baseModel<EventType.MissedInjection, {
}>;
export type VisitModel = baseModel<EventType.Visit, {
}>;

export type EventModel =
  | BleedingModel
  | InjectionModel
  | OperationModel
  | InspectionModel
  | MissedInjectionModel
  | NoteModel
  | PhysiotherapyModel
  | SportModel
  | VisitModel

export interface EventDrugJoinModel {
  $id?: string;
  $eventId?: string;
  $drugId?: string;
  $drug?: Partial<DrugModel>;
  expiresAt?: Date;
  lotNo?: string;
  serialNo?: string;
}

// The order is important because of the filtering on events
export enum EventType {
  Injection = 'injection',
  Physiotherapy = 'physiotherapy',
  MissedInjection = 'missedInjection',
  Visit = 'visit',
  Note = 'note',
  Operation = 'operation',
  Sport = 'sport',
  Bleeding = 'bleeding',
  Inspection = 'inspection',
}

export enum EventStatus {
  Draft = 'draft',
  Pending = 'pending',
  Approved = 'approved',
  Rejected = 'rejected',
}

export enum EventInjectionReason {
  Prophylaxis = 'prophylaxis',
  Bleeding = 'bleeding',
  SpecialTreatment = 'specialTreatment',
}